import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ListingCard } from './SplitReportDashboard';
import {
  Search,
  Plus,
  ArrowLeft,
  Building2,
  LayoutGrid,
  Filter
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SplitReportListings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [mineOnly, setMineOnly] = useState(searchParams.get('mine') === 'true');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => { fetchListings(); }, [searchQuery, statusFilter, mineOnly, page]);

  const fetchListings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.set('q', searchQuery);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (mineOnly) params.set('mine', 'true');
      params.set('page', page.toString());
      params.set('limit', '12');
      const res = await fetch(`${API}/split-report/listings?${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setListings(data.listings || []);
        setTotal(data.total || 0);
        setTotalPages(data.pages || 1);
      }
    } catch (e) { console.error('Error:', e); }
    finally { setLoading(false); }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchListings();
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="split-report-listings">
      <div className="bg-gradient-to-r from-[#3D4F7A] to-[#2A3755] py-8">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-6xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/split-report" className="text-white/70 hover:text-white"><ArrowLeft className="w-5 h-5" /></Link>
              <div>
                <h1 className="font-heading text-3xl font-bold text-white">All Listings</h1>
                <p className="text-slate-300 mt-1">{total} listings found</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/dashboard">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10" data-testid="sr-dashboard-btn">
                  Dashboard
                </Button>
              </Link>
              <Link to="/split-report/new">
                <Button className="bg-teal-500 hover:bg-teal-600 text-white" data-testid="new-listing-btn">
                  <Plus className="w-4 h-4 mr-2" />New Listing
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-6xl py-6">
        {/* Search & Filters */}
        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <form onSubmit={handleSearch} className="flex gap-2 flex-1">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search by address, city, state, or zip..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3D4F7A]/20 focus:border-[#3D4F7A] bg-white"
                data-testid="listings-search-input"
              />
            </div>
            <Button type="submit" className="bg-[#3D4F7A] hover:bg-[#2A3755]" data-testid="listings-search-btn">Search</Button>
          </form>
          <div className="flex gap-2">
            {['all', 'Active', 'Pending', 'Sold'].map((s) => (
              <Button
                key={s}
                variant={statusFilter === s ? 'default' : 'outline'}
                size="sm"
                className={statusFilter === s ? 'bg-[#3D4F7A]' : ''}
                onClick={() => { setStatusFilter(s); setPage(1); }}
                data-testid={`filter-${s.toLowerCase()}`}
              >
                {s === 'all' ? 'All' : s}
              </Button>
            ))}
            <Button
              variant={mineOnly ? 'default' : 'outline'}
              size="sm"
              className={mineOnly ? 'bg-teal-500 hover:bg-teal-600' : ''}
              onClick={() => { setMineOnly(!mineOnly); setPage(1); }}
              data-testid="filter-mine"
            >
              My Listings
            </Button>
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3D4F7A] mx-auto"></div>
          </div>
        ) : listings.length === 0 ? (
          <Card className="border border-slate-200">
            <CardContent className="p-12 text-center">
              <Building2 className="w-16 h-16 text-slate-200 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No listings found</h3>
              <p className="text-slate-500 mb-4">Try adjusting your search or filters.</p>
              <Link to="/split-report/new">
                <Button className="bg-teal-500 hover:bg-teal-600"><Plus className="w-4 h-4 mr-2" />Add New Listing</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {listings.map((listing) => (
                <ListingCard key={listing.listing_id} listing={listing} />
              ))}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <Button variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
                <span className="text-sm text-slate-600">Page {page} of {totalPages}</span>
                <Button variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
